import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Cpu,
  FileText,
  Music,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  useAdminBook,
  useBookAction,
  useDeleteAnecdote,
  useGenerateBookAi,
  useDeleteMedium,
  useGenerateBookPdf,
  usePurgeBookAttachment,
  useResendAnecdote,
  useResetChapterAudio,
  useUpdateAdminBook,
  useUpdateAnecdote,
  useUpdateAnswer,
  useUpdateGeneratedChapter,
} from "../../api/admin";
import {
  AdminAiBatch,
  AdminAnecdote,
  AdminAnswer,
  AdminAttachmentName,
  AdminBookDetail as AdminBookDetailData,
  AdminBookUpdateParams,
  AdminGeneratedChapter,
} from "../../types/admin";
import { formatBytes, formatDateTime } from "../../utils/dateUtils";
import { notify } from "./notify";
import {
  Badge,
  BookStatusBadge,
  bookStatusLabel,
  Button,
  Card,
  Empty,
  Field,
  Loading,
  NumberField,
  PageHeader,
  SelectField,
  TextArea,
} from "./ui";

const GENERATING_STATUSES = [
  "ai_generating",
  "pdf_generating",
  "audio_generating",
];

const ATTACHMENT_LABELS: Record<AdminAttachmentName, string> = {
  cover_image: "Image de couverture",
  content_pdf: "PDF contenu (impression)",
  cover_pdf: "PDF couverture (impression)",
  numeric_pdf: "PDF numérique",
  zip_file: "ZIP audiobook",
};

const TABS = [
  ["control", "Pilotage"],
  ["chapters", "Chapitres"],
  ["answers", "Réponses"],
  ["anecdotes", "Anecdotes"],
] as const;

type Tab = (typeof TABS)[number][0];

export const AdminBookDetail = () => {
  const { id } = useParams();
  const bookId = Number(id);
  const navigate = useNavigate();
  const { data, isLoading } = useAdminBook(bookId);
  const [tab, setTab] = useState<Tab>("control");

  if (isLoading || !data) return <Loading />;

  const { book, user } = data;
  const counts: Record<Tab, number | null> = {
    control: null,
    chapters: data.generated_chapters.length,
    answers: data.answers.length,
    anecdotes: data.anecdotes.length,
  };

  return (
    <>
      <button
        onClick={() => navigate(`/admin/users/${user.id}`)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> {user.full_name || user.email}
      </button>

      <PageHeader
        title={book.title || `Livre #${book.id}`}
        subtitle={`Livre #${book.id} · ${user.email} · mis à jour le ${formatDateTime(book.updated_at)}`}
        action={<BookStatusBadge status={book.status} />}
      />

      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {TABS.map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${
              tab === value
                ? "border-indigo-600 text-indigo-700"
                : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {label}
            {counts[value] !== null && (
              <span className="ml-1.5 text-xs text-gray-400">
                {counts[value]}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "control" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <UnblockCard data={data} />
          <FilesCard data={data} />
          <InfoCard data={data} />
          <AudioConfigCard data={data} />
        </div>
      )}
      {tab === "chapters" && <ChaptersTab data={data} />}
      {tab === "answers" && <AnswersTab data={data} />}
      {tab === "anecdotes" && <AnecdotesTab data={data} />}
    </>
  );
};

// UNBLOCK ====================================================================
const UnblockCard = ({ data }: { data: AdminBookDetailData }) => {
  const { book } = data;
  const update = useUpdateAdminBook(book.id);
  const generatePdf = useGenerateBookPdf(book.id);
  const generateAi = useGenerateBookAi(book.id);
  const generateAudio = useBookAction(book.id, "generate_audio");
  const validateAudio = useBookAction(book.id, "validate_audio");
  const generateZip = useBookAction(book.id, "generate_zip");
  const retrieveAi = useBookAction(book.id, "retrieve_ai");
  const [status, setStatus] = useState(book.status);
  const [size, setSize] = useState(data.pdf_default_size);
  const [aiModel, setAiModel] = useState(data.claude.model);
  const [aiEffort, setAiEffort] = useState(data.claude.effort);
  const efforts = data.claude.models[aiModel]?.efforts ?? [];

  // An effort the newly picked model doesn't support falls back to its default
  const pickModel = (model: string) => {
    setAiModel(model);
    const config = data.claude.models[model];
    if (config && !config.efforts.includes(aiEffort)) {
      setAiEffort(config.default_effort);
    }
  };

  useEffect(() => setStatus(book.status), [book.status]);

  const isGenerating = GENERATING_STATUSES.includes(book.status);

  return (
    <Card title="Débloquer le livre">
      <div className="p-5 space-y-5">
        {data.last_pdf_failure && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
            <strong>
              La génération du PDF a échoué le{" "}
              {formatDateTime(data.last_pdf_failure.at)}.
            </strong>{" "}
            Relancer sur une machine plus grosse ne suffira pas si l'erreur
            vient du contenu du livre :
            <pre className="mt-1 whitespace-pre-wrap break-all font-mono">
              {data.last_pdf_failure.error}
            </pre>
          </div>
        )}

        {data.last_ai_failure && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800">
            <strong>
              La génération IA a échoué le{" "}
              {formatDateTime(data.last_ai_failure.at)}.
            </strong>{" "}
            Relancer avec un autre modèle ne suffira pas si l'erreur vient de la
            requête ou du contenu du livre :
            <pre className="mt-1 whitespace-pre-wrap break-all font-mono">
              {data.last_ai_failure.error}
            </pre>
          </div>
        )}

        {isGenerating && <GeneratingNotice data={data} />}

        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">
            PDF : relancer sur une machine dédiée
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectField
                label="Taille du dyno"
                value={size}
                onChange={setSize}
                options={data.pdf_sizes.map((s) => [
                  s,
                  s === data.pdf_default_size ? `${s} (défaut)` : s,
                ])}
              />
            </div>
            <Button
              variant="primary"
              onClick={() =>
                generatePdf.mutate(size, notify(`PDF relancé sur ${size}`))
              }
              disabled={generatePdf.isLoading}
              confirm={`Relancer la génération du PDF sur un dyno ${size} ?`}
            >
              <FileText className="w-4 h-4" /> Relancer le PDF
            </Button>
          </div>
        </div>

        <div>
          <p className="text-xs font-medium text-gray-500 mb-2">
            IA : réécrire le livre
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectField
                label="Modèle"
                value={aiModel}
                onChange={pickModel}
                options={Object.entries(data.claude.models).map(
                  ([id, model]) => [
                    id,
                    `${model.label}${id === data.claude.model ? " (réglage)" : ""}`,
                  ],
                )}
              />
            </div>
            <div className="w-32">
              <SelectField
                label="Effort"
                value={aiEffort}
                onChange={setAiEffort}
                options={efforts.map((e) => [e, e])}
              />
            </div>
            <Button
              variant="primary"
              onClick={() =>
                generateAi.mutate(
                  { model: aiModel, effort: aiEffort },
                  notify(`Génération IA relancée avec ${aiModel}`),
                )
              }
              disabled={generateAi.isLoading}
              confirm={`Relancer la génération IA avec ${aiModel} (effort ${aiEffort}) ? Le contenu des chapitres sera réécrit.${
                data.ai_batch && data.ai_batch.status !== "ended"
                  ? " Le batch Claude en cours sera abandonné (et reste facturé)."
                  : ""
              }`}
            >
              <Cpu className="w-4 h-4" /> Relancer l'IA
            </Button>
          </div>
          {data.ai_batch && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs text-gray-600">
              <AiBatchState batch={data.ai_batch} />
              <Button
                onClick={() =>
                  retrieveAi.mutate(
                    undefined,
                    notify("Récupération des résultats lancée"),
                  )
                }
                disabled={
                  retrieveAi.isLoading || data.ai_batch.status !== "ended"
                }
                confirm="Récupérer les résultats du batch déjà payé ? Les chapitres seront remplacés par ceux du batch."
              >
                <RefreshCw className="w-4 h-4" /> Récupérer les résultats
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() =>
              generateAudio.mutate(
                undefined,
                notify("Génération audio relancée"),
              )
            }
            disabled={generateAudio.isLoading}
            confirm="Relancer l'audiobook ? Seuls les chapitres sans audio seront générés."
          >
            <Music className="w-4 h-4" /> Relancer l'audio
          </Button>
          <Button
            onClick={() =>
              validateAudio.mutate(undefined, notify("Audiobook validé"))
            }
            disabled={validateAudio.isLoading}
            confirm="Valider l'audiobook ? La voix custom sera supprimée chez ElevenLabs."
          >
            Valider l'audio
          </Button>
          <Button
            onClick={() =>
              generateZip.mutate(undefined, notify("ZIP en cours"))
            }
            disabled={generateZip.isLoading}
          >
            Régénérer le ZIP
          </Button>
        </div>

        <div className="flex items-end gap-2 pt-2 border-t border-gray-100">
          <div className="flex-1">
            <SelectField
              label="Forcer le statut (sans lancer de job)"
              value={status}
              onChange={setStatus}
              options={data.statuses.map((s) => [s, bookStatusLabel(s)])}
            />
          </div>
          <Button
            onClick={() =>
              update.mutate({ status }, notify(`Statut forcé à ${status}`))
            }
            disabled={update.isLoading || status === book.status}
            confirm={`Passer le livre de ${book.status} à ${status} ?`}
          >
            Appliquer
          </Button>
        </div>
      </div>
    </Card>
  );
};

/** What is happening while a step runs, and what to do about it. */
const GeneratingNotice = ({ data }: { data: AdminBookDetailData }) => {
  const { book, ai_batch: batch } = data;
  const since = formatDateTime(book.updated_at);

  if (book.status === "ai_generating" && batch && !batch.error) {
    return (
      <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
        {batch.status === "ended" ? (
          <>
            Claude a terminé le batch : les chapitres sont récupérés
            automatiquement dans les 5 minutes. Sinon, utilise « Récupérer les
            résultats » ci-dessous.
          </>
        ) : (
          <>
            Claude écrit le livre (batch lancé le{" "}
            {batch.created_at ? formatDateTime(batch.created_at) : since}). Cela
            prend en général moins d'une heure, jusqu'à 24 h. Les chapitres sont
            récupérés automatiquement toutes les 5 minutes : rien à faire, et ne
            relance pas l'IA.
          </>
        )}
      </div>
    );
  }

  if (book.status === "ai_generating") {
    return (
      <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
        {bookStatusLabel(book.status)} depuis le {since}, sans batch Claude pour
        l'instant : Claude écrit le plan du livre (quelques minutes au plus).
        Au-delà, regarde la page Jobs, puis relance l'IA.
      </div>
    );
  }

  return (
    <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
      {bookStatusLabel(book.status)} depuis le {since}. Si rien ne bouge,
      regarde les dynos (page Infra) et les jobs, puis relance l'étape
      ci-dessous.
    </div>
  );
};

const BATCH_STATUS_LABELS: Record<
  NonNullable<AdminAiBatch["status"]>,
  string
> = {
  in_progress: "en cours",
  canceling: "en cours d'annulation",
  ended: "terminé",
};

const AiBatchState = ({ batch }: { batch: AdminAiBatch }) => {
  if (batch.error) {
    return (
      <span>
        Batch <span className="font-mono">{batch.id}</span> illisible :{" "}
        {batch.error}
      </span>
    );
  }
  const counts = batch.request_counts;
  return (
    <span>
      Batch <span className="font-mono">{batch.id}</span>{" "}
      {batch.status ? BATCH_STATUS_LABELS[batch.status] : ""}
      {counts &&
        ` · ${counts.succeeded} réussie(s), ${counts.errored + counts.expired + counts.canceled} en échec, ${counts.processing} en cours`}
      {batch.status === "ended"
        ? batch.results_until &&
          ` · résultats disponibles jusqu'au ${formatDateTime(batch.results_until)}`
        : batch.expires_at &&
          ` · doit se terminer avant le ${formatDateTime(batch.expires_at)}, sinon les requêtes restantes expirent`}
    </span>
  );
};

// FILES ======================================================================
const FilesCard = ({ data }: { data: AdminBookDetailData }) => {
  const { book } = data;
  const purge = usePurgeBookAttachment(book.id);

  return (
    <Card title="Fichiers">
      <ul className="divide-y divide-gray-100">
        {(Object.keys(ATTACHMENT_LABELS) as AdminAttachmentName[]).map(
          (name) => {
            const file = book.attachments[name];
            return (
              <li
                key={name}
                className="flex items-center justify-between px-5 py-2.5 text-sm"
              >
                <div className="min-w-0">
                  <p className="font-medium text-gray-800">
                    {ATTACHMENT_LABELS[name]}
                  </p>
                  {file ? (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-indigo-600 hover:underline truncate block"
                    >
                      {file.filename} · {formatBytes(file.byte_size)} ·{" "}
                      {formatDateTime(file.created_at)}
                    </a>
                  ) : (
                    <p className="text-xs text-gray-400">Absent</p>
                  )}
                </div>
                {file && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      purge.mutate(name, notify("Fichier supprimé"))
                    }
                    disabled={purge.isLoading}
                    confirm={`Supprimer ${ATTACHMENT_LABELS[name]} ?`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          },
        )}
        {book.user_voice_samples.map((sample) => (
          <li key={sample.id} className="px-5 py-2.5 text-sm">
            <p className="font-medium text-gray-800">Échantillon de voix</p>
            <audio controls src={sample.url} className="w-full h-8 mt-1" />
          </li>
        ))}
      </ul>
    </Card>
  );
};

// INFO =======================================================================
const InfoCard = ({ data }: { data: AdminBookDetailData }) => {
  const { book } = data;
  const update = useUpdateAdminBook(book.id);
  const [form, setForm] = useState<AdminBookUpdateParams>({});

  useEffect(() => {
    setForm({
      title: book.title ?? "",
      author: book.author ?? "",
      author_birthdate: book.author_birthdate ?? "",
      publication_date: book.publication_date ?? "",
      summary: book.summary ?? "",
      total_page_count: book.total_page_count ?? 0,
      cover_text_color: book.cover_text_color ?? "",
      cover_bg_color: book.cover_bg_color ?? "",
    });
  }, [book]);

  const set = (key: keyof AdminBookUpdateParams) => (value: string | number) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <Card title="Informations">
      <div className="p-5 space-y-3">
        <Field
          label="Titre"
          value={String(form.title ?? "")}
          onChange={set("title")}
        />
        <Field
          label="Auteur"
          value={String(form.author ?? "")}
          onChange={set("author")}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            type="date"
            label="Naissance de l'auteur"
            value={String(form.author_birthdate ?? "")}
            onChange={set("author_birthdate")}
          />
          <Field
            type="date"
            label="Publication"
            value={String(form.publication_date ?? "")}
            onChange={set("publication_date")}
          />
        </div>
        <TextArea
          label="Résumé"
          rows={3}
          value={String(form.summary ?? "")}
          onChange={set("summary")}
        />
        <div className="grid grid-cols-3 gap-3">
          <NumberField
            label="Pages"
            value={Number(form.total_page_count ?? 0)}
            onChange={set("total_page_count")}
          />
          <Field
            label="Couleur texte"
            value={String(form.cover_text_color ?? "")}
            onChange={set("cover_text_color")}
          />
          <Field
            label="Couleur fond"
            value={String(form.cover_bg_color ?? "")}
            onChange={set("cover_bg_color")}
          />
        </div>
        <p className="text-xs text-gray-400">
          Langue : {book.language ?? "—"} · Style :{" "}
          {book.narration_style ?? "—"} · Couverture : {book.cover_type ?? "—"}
        </p>
        <Button
          variant="primary"
          onClick={() => update.mutate(form, notify("Livre mis à jour"))}
          disabled={update.isLoading}
        >
          Enregistrer
        </Button>
      </div>
    </Card>
  );
};

// AUDIO CONFIG ===============================================================
const AudioConfigCard = ({ data }: { data: AdminBookDetailData }) => {
  const { book } = data;
  const audioUrl = book.audio_book_token
    ? `${window.location.origin}/audio_book?token=${book.audio_book_token}`
    : null;

  return (
    <Card title="Audiobook & technique">
      <div className="p-5 space-y-3 text-xs">
        <pre className="bg-gray-50 rounded-md p-3 overflow-x-auto text-gray-700">
          {JSON.stringify(book.audio_book_config, null, 2)}
        </pre>
        <p className="text-gray-500 break-all">
          Token audio : {book.audio_book_token ?? "—"}
          {audioUrl && (
            <>
              {" · "}
              <a
                href={audioUrl}
                target="_blank"
                rel="noreferrer"
                className="text-indigo-600 hover:underline"
              >
                page publique
              </a>
            </>
          )}
        </p>
        <p className="text-gray-500">
          Modèle IA ayant écrit le livre : {book.ai_model ?? "—"}
        </p>
        <p className="text-gray-500 break-all">
          Batch Anthropic : {book.batch_id ?? "—"}
        </p>
        <p className="text-gray-500 break-all">
          Batches messages : {JSON.stringify(book.msg_batch_ids) ?? "—"}
        </p>
      </div>
    </Card>
  );
};

// CHAPTERS ===================================================================
const ChaptersTab = ({ data }: { data: AdminBookDetailData }) =>
  data.generated_chapters.length === 0 ? (
    <Card>
      <Empty>Aucun chapitre généré.</Empty>
    </Card>
  ) : (
    <div className="space-y-3">
      {data.generated_chapters.map((chapter) => (
        <ChapterItem key={chapter.id} bookId={data.book.id} chapter={chapter} />
      ))}
    </div>
  );

const ChapterItem = ({
  bookId,
  chapter,
}: {
  bookId: number;
  chapter: AdminGeneratedChapter;
}) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(chapter.title ?? "");
  const [content, setContent] = useState(chapter.content ?? "");
  const update = useUpdateGeneratedChapter(bookId);
  const resetAudio = useResetChapterAudio(bookId);
  const deleteMedium = useDeleteMedium(bookId);

  useEffect(() => {
    setTitle(chapter.title ?? "");
    setContent(chapter.content ?? "");
  }, [chapter]);

  return (
    <Card>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
          {open ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          {chapter.chapter_number}. {chapter.title || "Sans titre"}
        </span>
        <span className="flex items-center gap-2 text-xs text-gray-500">
          {chapter.word_count ?? 0} mots
          {chapter.media.length > 0 && (
            <Badge color="blue">{chapter.media.length} média(s)</Badge>
          )}
          {chapter.is_audio_generated && <Badge color="purple">Audio</Badge>}
          {chapter.completed && <Badge color="green">Terminé</Badge>}
        </span>
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">
          {editing ? (
            <>
              <Field label="Titre" value={title} onChange={setTitle} />
              <TextArea
                label="Contenu (HTML)"
                rows={18}
                mono
                value={content}
                onChange={setContent}
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={() =>
                    update.mutate(
                      { id: chapter.id, params: { title, content } },
                      notify("Chapitre enregistré", () => setEditing(false)),
                    )
                  }
                  disabled={update.isLoading}
                >
                  Enregistrer
                </Button>
                <Button onClick={() => setEditing(false)}>Annuler</Button>
              </div>
            </>
          ) : (
            <>
              <div
                className="prose prose-sm max-w-none text-gray-700 max-h-96 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: chapter.content ?? "" }}
              />
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setEditing(true)}>
                  Modifier le texte
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    update.mutate(
                      {
                        id: chapter.id,
                        params: { completed: !chapter.completed },
                      },
                      notify(
                        chapter.completed
                          ? "Chapitre rouvert"
                          : "Chapitre terminé",
                      ),
                    )
                  }
                >
                  {chapter.completed
                    ? "Marquer non terminé"
                    : "Marquer terminé"}
                </Button>
              </div>
            </>
          )}

          {chapter.audio_url && (
            <div className="flex items-center gap-2">
              <audio controls src={chapter.audio_url} className="flex-1 h-8" />
              <Button
                size="sm"
                variant="danger"
                onClick={() =>
                  resetAudio.mutate(chapter.id, notify("Audio supprimé"))
                }
                disabled={resetAudio.isLoading}
                confirm="Supprimer l'audio de ce chapitre ? Il sera régénéré au prochain lancement de l'audiobook."
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refaire l'audio
              </Button>
            </div>
          )}

          {chapter.media.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {chapter.media.map((medium) => (
                <div
                  key={medium.id}
                  className="border border-gray-100 rounded-md p-2 text-xs space-y-1"
                >
                  {medium.file_type === "image" && medium.url ? (
                    <a href={medium.url} target="_blank" rel="noreferrer">
                      <img
                        src={medium.url}
                        alt={medium.title ?? ""}
                        className="w-full h-28 object-cover rounded"
                      />
                    </a>
                  ) : medium.url &&
                    ["audio", "audio_chapter"].includes(medium.file_type) ? (
                    <audio controls src={medium.url} className="w-full h-8" />
                  ) : medium.url ? (
                    <video
                      controls
                      src={medium.url}
                      className="w-full h-28 rounded"
                    />
                  ) : (
                    <p className="text-red-500">Fichier manquant</p>
                  )}
                  <p className="text-gray-500 truncate">
                    {medium.file_type} · {formatBytes(medium.byte_size)}
                  </p>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() =>
                      deleteMedium.mutate(medium.id, notify("Média supprimé"))
                    }
                    disabled={deleteMedium.isLoading}
                    confirm="Supprimer ce média ?"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ANSWERS ====================================================================
const AnswersTab = ({ data }: { data: AdminBookDetailData }) => {
  if (data.answers.length === 0) {
    return (
      <Card>
        <Empty>Aucune réponse.</Empty>
      </Card>
    );
  }

  const byChapter = data.answers.reduce<Record<string, AdminAnswer[]>>(
    (acc, answer) => {
      const key = String(answer.chapter_number ?? "?");
      (acc[key] ??= []).push(answer);
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-6">
      {Object.entries(byChapter).map(([chapter, answers]) => (
        <Card key={chapter} title={`Chapitre ${chapter}`}>
          <ul className="divide-y divide-gray-100">
            {answers.map((answer) => (
              <AnswerItem
                key={answer.id}
                bookId={data.book.id}
                answer={answer}
              />
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
};

const AnswerItem = ({
  bookId,
  answer,
}: {
  bookId: number;
  answer: AdminAnswer;
}) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(answer.content ?? "");
  const update = useUpdateAnswer(bookId);

  useEffect(() => setContent(answer.content ?? ""), [answer.content]);

  return (
    <li className="px-5 py-3 space-y-1.5">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-gray-500">
          Q{answer.question_number}. {answer.question}
        </p>
        <span className="flex items-center gap-2 shrink-0">
          {answer.completed ? (
            <Badge color="green">Répondu</Badge>
          ) : (
            <Badge color="gray">Vide</Badge>
          )}
          {!editing && (
            <Button size="sm" onClick={() => setEditing(true)}>
              Modifier
            </Button>
          )}
        </span>
      </div>
      {editing ? (
        <>
          <TextArea value={content} onChange={setContent} rows={5} />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                update.mutate(
                  {
                    id: answer.id,
                    params: { content, completed: content.trim().length > 0 },
                  },
                  notify("Réponse enregistrée", () => setEditing(false)),
                )
              }
              disabled={update.isLoading}
            >
              Enregistrer
            </Button>
            <Button size="sm" onClick={() => setEditing(false)}>
              Annuler
            </Button>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-800 whitespace-pre-line">
          {answer.content || <span className="text-gray-400">—</span>}
        </p>
      )}
    </li>
  );
};

// ANECDOTES ==================================================================
const ANECDOTE_STATUS_COLORS: Record<
  string,
  "yellow" | "blue" | "green" | "red" | "gray"
> = {
  pending: "yellow",
  submitted: "blue",
  accepted: "green",
  rejected: "red",
  expired: "gray",
};

const AnecdotesTab = ({ data }: { data: AdminBookDetailData }) =>
  data.anecdotes.length === 0 ? (
    <Card>
      <Empty>Aucune anecdote demandée.</Empty>
    </Card>
  ) : (
    <div className="space-y-3">
      {data.anecdotes.map((anecdote) => (
        <AnecdoteItem
          key={anecdote.id}
          bookId={data.book.id}
          anecdote={anecdote}
        />
      ))}
    </div>
  );

const AnecdoteItem = ({
  bookId,
  anecdote,
}: {
  bookId: number;
  anecdote: AdminAnecdote;
}) => {
  const update = useUpdateAnecdote(bookId);
  const resend = useResendAnecdote(bookId);
  const remove = useDeleteAnecdote(bookId);
  const [status, setStatus] = useState(anecdote.status);
  const [expiresAt, setExpiresAt] = useState(anecdote.expires_at.slice(0, 10));
  const [content, setContent] = useState(anecdote.content ?? "");

  useEffect(() => {
    setStatus(anecdote.status);
    setExpiresAt(anecdote.expires_at.slice(0, 10));
    setContent(anecdote.content ?? "");
  }, [anecdote]);

  return (
    <Card>
      <div className="p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900">
              {anecdote.friend_first_name} {anecdote.friend_last_name}
            </p>
            <p className="text-xs text-gray-500">
              {anecdote.friend_email} · chapitre {anecdote.chapter_number} ·
              demandée le {formatDateTime(anecdote.created_at)}
            </p>
            <p className="text-xs text-gray-400">
              Soumise : {formatDateTime(anecdote.submitted_at)} · dernière
              relance : {formatDateTime(anecdote.last_reminder_sent_at)} ·
              marketing : {anecdote.marketing_accepted ? "oui" : "non"}
            </p>
          </div>
          <Badge color={ANECDOTE_STATUS_COLORS[anecdote.status] ?? "gray"}>
            {anecdote.status}
          </Badge>
        </div>

        {anecdote.custom_message && (
          <p className="text-xs text-gray-500 italic">
            Message : {anecdote.custom_message}
          </p>
        )}
        {anecdote.photo_url && (
          <a href={anecdote.photo_url} target="_blank" rel="noreferrer">
            <img
              src={anecdote.photo_url}
              alt=""
              className="h-32 rounded object-cover"
            />
          </a>
        )}

        <TextArea
          label="Contenu"
          value={content}
          onChange={setContent}
          rows={4}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Statut"
            value={status}
            onChange={setStatus}
            options={Object.keys(ANECDOTE_STATUS_COLORS).map((s) => [s, s])}
          />
          <Field
            type="date"
            label="Expire le"
            value={expiresAt}
            onChange={setExpiresAt}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            onClick={() =>
              update.mutate(
                {
                  id: anecdote.id,
                  params: { status, content, expires_at: expiresAt },
                },
                notify("Anecdote enregistrée"),
              )
            }
            disabled={update.isLoading}
          >
            Enregistrer
          </Button>
          <Button
            onClick={() =>
              resend.mutate(anecdote.id, notify("Demande renvoyée"))
            }
            disabled={resend.isLoading}
          >
            <Send className="w-4 h-4" /> Renvoyer la demande
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              remove.mutate(anecdote.id, notify("Anecdote supprimée"))
            }
            disabled={remove.isLoading}
            confirm="Supprimer définitivement cette anecdote ?"
          >
            <Trash2 className="w-4 h-4" /> Supprimer
          </Button>
        </div>
      </div>
    </Card>
  );
};
